import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { ColumnDefinitionsPropTypes } from '../../shared/propTypes/common';
import ImportGrid from './importGrid.component';

import Button from '../../shared/button.component';
import Attachments from '../../shared/forms/linkAttachments.component';

import { excelFileExtensions } from '../../shared/fileUpload/constants';
import { appendAddOrUpdateInfo, getImportStats, compareAddOrUpdate } from './importHelpers';
import { generateAssetPath } from '../../utils/assetsHelpers';
import Spinner from '../../shared/spinner.component';
import { waitForSpinnerToShowUp } from '../../shared/cursorHelpers';
import NavigationPrompt, { DEFAULT_MESSAGE } from '../../shared/navigationPrompt.component';

const AdminEntityImport = ({
  pageTitle,
  entities,
  uploadedEntities,
  uploadingEntities,
  selectFileToUpload,
  importEntities,
  templateFileName,
  templatePath,
  generateTemplate,
  disableGeneratingTemplate,
  disableImporting,
  idFieldComparator,
  idField,
  columnDefs,
  ignoredFields,
  entitiesValuesComparator,
  onDiscard,
  isSubmitDisabled = false,
  promptOnExit = false,
  importProgressBarComponent,
}) => {
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const submitImport = useCallback(() => {
    const extendedImportedEntities = appendAddOrUpdateInfo(
      uploadedEntities,
      entities,
      { modifyEntity: false, idField, idFieldComparator, ignoredFields, entitiesValuesComparator }
    );

    importEntities(extendedImportedEntities);
  }, [entities, uploadedEntities, importEntities, idFieldComparator, idField, entitiesValuesComparator, ignoredFields]);

  const extendedUploadedEntities = useMemo(() => appendAddOrUpdateInfo(
    uploadedEntities,
    entities,
    { modifyEntity: true, idField, idFieldComparator, ignoredFields, entitiesValuesComparator }
  )
    .sort((rowA, rowB) => compareAddOrUpdate(rowA.addOrUpdate, rowB.addOrUpdate)),
  [uploadedEntities, entities, idField, idFieldComparator, ignoredFields, entitiesValuesComparator]);

  const importStats = useMemo(() => getImportStats(extendedUploadedEntities), [extendedUploadedEntities]);

  const shouldSubmitButtonBeDisabled = !importEntities
    || !importStats.loaded
    || !!importStats.errors
    || (!importStats.updates && !importStats.adds);

  const shouldShowNavigationPrompt = promptOnExit && extendedUploadedEntities.length > 0;

  const downloadTemplate = useCallback(
    () => {
      setIsGeneratingTemplate(true);
      waitForSpinnerToShowUp({
        action: generateTemplate,
        callback: () => {
          setIsGeneratingTemplate(false);
        },
      });
    },
    [generateTemplate]
  );

  const downloadTemplateButton = useMemo(() => generateTemplate
    ? (
      <button
        className="hamburger-action link-button generate-template-button"
        tabIndex="2"
        onClick={downloadTemplate}
        disabled={disableGeneratingTemplate}
      >
        {isGeneratingTemplate
          ? (
            <span>
              <Spinner small /> exporting
            </span>
          )
          : ' click to download '
        }
      </button>
    )
    : (
      <a
        className="hamburger-action"
        role="button"
        tabIndex="1"
        download={templateFileName}
        href={generateAssetPath(templatePath)}
      >
        {' click to download '}
      </a>
    ), [
    generateTemplate,
    disableGeneratingTemplate,
    templatePath,
    templateFileName,
    isGeneratingTemplate,
    downloadTemplate,
  ]);

  return (
    <div>
      { importProgressBarComponent }
      { extendedUploadedEntities.length
        ? (
          <div className="text-center">
            {`${pageTitle} load complete. ${importStats.loaded} records loaded`}
            {`, ${importStats.errors} errors`}
            {`, ${importStats.adds} adds`}
            {`, ${importStats.updates} updates.`}
          </div>
        ) : null
      }
      <ImportGrid
        data={extendedUploadedEntities}
        hasValidationErrors={importStats.errors !== 0}
        className="admin-import-grid"
        columnDefs={columnDefs}
        isLoading={uploadingEntities}
      />
      {selectFileToUpload && ((templateFileName && templatePath) || generateTemplate)
        ? (
          <div className="d-flex flex-row align-items-center page-content-container">
            <Attachments
              maxFileSize={10}
              headerText={''}
              getRawFile={selectFileToUpload}
              acceptTypes={excelFileExtensions}
              downloadTemplateButton={downloadTemplateButton}
              disabled={disableImporting}
            />
            <Button
              type="button"
              kind="primary"
              disabled={shouldSubmitButtonBeDisabled}
              className="ml-auto"
              onClick={submitImport}
            >
              Submit
            </Button>
          </div>
        )
        : null
      }
      <NavigationPrompt
        show={shouldShowNavigationPrompt}
        message={
          onDiscard
            ? 'Changes you made are not submitted. You can leave them or discard them.'
            : DEFAULT_MESSAGE
        }
        onDiscard={onDiscard}
        isSubmitDisabled={isSubmitDisabled}
      />
    </div>
  );
};

AdminEntityImport.propTypes = {
  pageTitle: PropTypes.string.isRequired,
  entities: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
  ]))).isRequired,
  uploadedEntities: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.shape({
      validationErrors: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string,
        message: PropTypes.string,
      })),
    }),
  ]))).isRequired,
  uploadingEntities: PropTypes.bool.isRequired,
  selectFileToUpload: PropTypes.func,
  importEntities: PropTypes.func,
  templateFileName: PropTypes.string,
  templatePath: PropTypes.string,
  generateTemplate: PropTypes.func,
  disableGeneratingTemplate: PropTypes.bool,
  disableImporting: PropTypes.bool,
  idFieldComparator: PropTypes.func,
  idField: PropTypes.string.isRequired,
  columnDefs: ColumnDefinitionsPropTypes.isRequired,
  ignoredFields: PropTypes.arrayOf(PropTypes.string.isRequired),
  entitiesValuesComparator: PropTypes.func,
  promptOnExit: PropTypes.bool,
  onDiscard: PropTypes.func,
  isSubmitDisabled: PropTypes.bool,
  importProgressBarComponent: PropTypes.node,
};

export default AdminEntityImport;
